import { useCallback } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'

import { TextFieldInput, GridItem } from '@sequencehq/core-components'
import {
  Controls,
  FieldGrid,
  FieldLabel,
  SecondaryOptions,
  Title
} from '../../common/layout'
import {
  DescriptionField,
  LedgerIntegrationFields,
  PercentageRatesToggle,
  QuantityField,
  RateField,
  RecognitionMethodField,
  RevenueClassificationField,
  ServicePeriodField,
  TaxCategoryField
} from '../../common/fields'
import { AddSubItemsToggle, DiscountField, SubItems } from './fields'
import {
  descriptionAtom,
  descriptionEnabledAtom,
  discountEnabledAtom,
  titleAtom,
  quantityAtom,
  rateAtom,
  rateDisplayAtom,
  revenueRecognitionMethodAtom,
  servicePeriodAtom,
  subItemsEnabledAtom,
  taxCategoryIdAtom,
  formStateAtom,
  externalIdsAtom,
  productIdAtom,
  revenueClassificationAtom
} from './state.atoms'
import { validateFormAtom, validationResultsAtom } from './validation.atoms'
import { lineItemGroupsAtom } from 'Invoices/atoms/invoiceEditing.atom'
import { formStateToLineItemGroup } from './adapters'
import { BaseConfig, CreateLineItemGroupConfig } from '../../types'
import { setDefaultExternalIds } from '../../common/adapters'
import { isFieldInvalid } from '../../common/validation'
import { secondaryOptionsVisibleAtom } from '../../common/atoms'
import { showAddLineItemGroupDropdownAtom } from 'Invoices/components/CreateEditShared/SectionLineItems'
import { TaxRateModals, useClientInvoiceTaxModals } from 'Invoices/components'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

export const CreateLineItemGroup = (
  props: CreateLineItemGroupConfig & BaseConfig
) => {
  const flags = useFeatureFlags()

  useHydrateAtoms([
    [titleAtom, props.defaultTitle ?? ''],
    [taxCategoryIdAtom, props.defaultTaxCategoryId ?? ''],
    [externalIdsAtom, setDefaultExternalIds(props.customerLedgerIntegrations)],
    [productIdAtom, props.productId],
    [servicePeriodAtom, props.invoiceBillingPeriodDates]
  ])

  const [title, setTitle] = useAtom(titleAtom)
  const [quantity, setQuantity] = useAtom(quantityAtom)
  const [rate, setRate] = useAtom(rateAtom)
  const subItemsEnabled = useAtomValue(subItemsEnabledAtom)
  const [rateDisplay, setRateDisplay] = useAtom(rateDisplayAtom)
  const [discountEnabled, setDiscountEnabled] = useAtom(discountEnabledAtom)
  const [servicePeriod, setServicePeriod] = useAtom(servicePeriodAtom)
  const [descriptionEnabled, setDescriptionEnabled] = useAtom(
    descriptionEnabledAtom
  )
  const [description, setDescription] = useAtom(descriptionAtom)
  const [taxCategoryId, setTaxCategoryId] = useAtom(taxCategoryIdAtom)
  const [revenueRecognitionMethod, setRevenueRecognitionMethod] = useAtom(
    revenueRecognitionMethodAtom
  )
  const [revenueClassification, setRevenueClassification] = useAtom(
    revenueClassificationAtom
  )
  const [externalIds, setExternalIds] = useAtom(externalIdsAtom)

  const validationResults = useAtomValue(validationResultsAtom)
  const validateForm = useSetAtom(validateFormAtom)
  const [lineItemGroups, setLineItemGroups] = useAtom(lineItemGroupsAtom)
  const formState = useAtomValue(formStateAtom)
  const [secondaryOptionsVisible, setSecondaryOptionsVisible] = useAtom(
    secondaryOptionsVisibleAtom
  )
  const setShowLineItemGroupDropdown = useSetAtom(
    showAddLineItemGroupDropdownAtom
  )

  const taxModalsHook = useClientInvoiceTaxModals({ taxCategoryId })

  const onSave = useCallback(
    ({ addAnother = false }: { addAnother?: boolean } = {}) => {
      const result = validateForm()

      if (!result || !lineItemGroups) return

      taxModalsHook.functions.onConfirmSubmit(() => {
        setLineItemGroups([
          ...lineItemGroups,
          formStateToLineItemGroup({
            ...formState,
            ...(flags.useRevRec
              ? {
                  revenueRecognitionMethod: formState.revenueRecognitionMethod,
                  revenueClassification: formState.revenueClassification
                }
              : {
                  revenueRecognitionMethod: undefined,
                  revenueClassification: undefined
                })
          })
        ])

        if (addAnother) {
          setShowLineItemGroupDropdown(true)
        }

        props.onDismiss()
      })()
    },
    [
      taxModalsHook.functions,
      props.onDismiss,
      validateForm,
      setLineItemGroups,
      formState,
      lineItemGroups,
      setShowLineItemGroupDropdown,
      flags.useRevRec
    ]
  )

  return (
    <>
      <Title>Add line item</Title>

      <FieldGrid>
        <GridItem gridColumn="span 2">
          <FieldLabel label="Name" validationErrors={validationResults?.title}>
            <TextFieldInput
              data-testid="lineItemEditor.name"
              placeholder="Name"
              value={title}
              onChange={e => setTitle(e.target.value)}
              autoFocus={!props.defaultTitle}
              isInvalid={isFieldInvalid(validationResults?.title)}
              data-1p-ignore
              data-lpignore
            />
          </FieldLabel>
        </GridItem>
        <GridItem>
          <FieldLabel label="Qty" textAlign="right">
            <QuantityField
              value={quantity}
              isDisabled={subItemsEnabled}
              onChange={setQuantity}
              rateDisplay={rateDisplay}
              isInvalid={isFieldInvalid(validationResults?.quantity)}
            />
          </FieldLabel>
        </GridItem>
        <GridItem>
          <FieldLabel label="Rate" textAlign="right">
            <RateField
              data-testid="lineItemEditor.amount"
              value={rate}
              isDisabled={subItemsEnabled}
              onChange={setRate}
              rateDisplay={rateDisplay}
              autoFocus={!!props.defaultTitle}
              isInvalid={isFieldInvalid(validationResults?.rate)}
            />
          </FieldLabel>
        </GridItem>

        <SubItems />
      </FieldGrid>

      <SecondaryOptions
        isVisible={secondaryOptionsVisible}
        setIsVisible={setSecondaryOptionsVisible}
        toggles={[
          <PercentageRatesToggle
            key="percentage-rates-toggle"
            rateDisplay={rateDisplay}
            setRateDisplay={setRateDisplay}
          />,
          <AddSubItemsToggle key="add-sub-items-toggle" />
        ]}
      >
        <SecondaryOptions.Group>
          <SecondaryOptions.ToggleField
            label="Discount"
            isVisible={discountEnabled}
            setIsVisible={setDiscountEnabled}
          >
            <DiscountField />
          </SecondaryOptions.ToggleField>

          <SecondaryOptions.ToggleField
            label="Description"
            isVisible={descriptionEnabled}
            setIsVisible={setDescriptionEnabled}
          >
            <DescriptionField
              data-testid="lineItemEditor.group.description"
              description={description}
              setDescription={setDescription}
              validationErrors={validationResults?.description}
            />
          </SecondaryOptions.ToggleField>
        </SecondaryOptions.Group>

        <SecondaryOptions.Group>
          <ServicePeriodField
            servicePeriod={servicePeriod}
            setServicePeriod={setServicePeriod}
          />

          <FieldLabel label="Tax category">
            <TaxCategoryField
              taxCategoryId={taxCategoryId}
              setTaxCategoryId={setTaxCategoryId}
              isInvalid={isFieldInvalid(validationResults?.taxCategoryId)}
            />
          </FieldLabel>

          <LedgerIntegrationFields
            customerLedgerIntegrations={props.customerLedgerIntegrations}
            externalIds={externalIds}
            setExternalIds={setExternalIds}
            validationResults={validationResults?.externalIds ?? []}
          />

          <RevenueClassificationField
            revenueClassification={revenueClassification}
            setRevenueClassification={setRevenueClassification}
          />
          <RecognitionMethodField
            revenueRecognitionMethod={revenueRecognitionMethod}
            setRevenueRecognitionMethod={setRevenueRecognitionMethod}
            revenueClassification={revenueClassification}
            servicePeriod={servicePeriod}
          />
        </SecondaryOptions.Group>
      </SecondaryOptions>

      <Controls>
        <Controls.Cancel onClick={props.onDismiss} />
        <Controls.SaveAndAddAnother
          onClick={() => onSave({ addAnother: true })}
        />
        <Controls.Confirm
          data-testid="lineItemEditor.save"
          onClick={onSave}
          keyboardShortcut={['Mod', 'Enter']}
        />
      </Controls>

      <TaxRateModals hook={taxModalsHook} />
    </>
  )
}
